<template>
  <div class="coach-note-page">
    <PatientHeader :patient="patientInfo" :refresh-handler="refreshAll" class="mb-4" />

    <!-- Modal chọn Patient ID -->
    <b-modal
      v-model="showPatientSelectModal"
      title="Chọn hồ sơ bệnh nhân"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      centered
      @ok="confirmSelectPatientId"
      ok-title="Xác nhận"
      ok-variant="success"
      cancel-title="Huỷ"
    >
      <p class="mb-3">Số điện thoại có nhiều hồ sơ. Vui lòng chọn hồ sơ cần xem:</p>
      <b-form-group>
        <b-form-radio
          v-for="pid in patientIdOptions"
          :key="pid"
          v-model="pendingExternalId"
          :value="pid"
          class="mb-2"
        >
          {{ pid }}
        </b-form-radio>
      </b-form-group>
    </b-modal>

    <div class="cn-layout">
      <!-- Main content -->
      <div class="cn-main">
        <b-tabs v-model="activeTab" class="cn-tabs">
          <b-tab title="Tuần hiện tại">
            <ProgramStatusBar
              :user-package-id="userPackageId"
              :is-update-permission="isUpdatePermission"
            />
            <Tab1WeekCurrent
              ref="tab1"
              :user-package-id="userPackageId"
              :week-number="selectedWeek"
              :week-list="weekList"
              :external-id="selectedExternalId"
              :is-view-permission="isViewPermission"
              :is-insert-permission="isInsertPermission"
              :is-update-permission="isUpdatePermission"
              :is-delete-permission="isDeletePermission"
              @change-week="onWeekSelect"
              @week-completed="loadWeekList"
            />
          </b-tab>
          <b-tab title="Tiến độ &amp; Lịch sử">
            <Tab2Progress :user-package-id="userPackageId" />
          </b-tab>
          <b-tab title="Tổng trạng &amp; Mục tiêu">
            <Tab3BaselineGoal
              :user-package-id="userPackageId"
              :his-baseline-data="hisBaselineData"
              :package-start-date="packageStartDate"
              :is-view-permission="isViewPermission"
              :is-insert-permission="isInsertPermission"
              :is-update-permission="isUpdatePermission"
              :is-delete-permission="isDeletePermission"
            />
          </b-tab>
        </b-tabs>
      </div>
      <div v-if="activeTab === 0" class="cn-sidebar">
        <ProgressTracker
          :week-list="weekList"
          :selected-week="selectedWeek"
          @select="onWeekSelect"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Tab1WeekCurrent from './components/Tab1WeekCurrent.vue';
import Tab2Progress from './components/Tab2Progress.vue';
import Tab3BaselineGoal from './components/Tab3BaselineGoal.vue';
import ProgressTracker from './components/ProgressTracker.vue';
import PatientHeader from './components/PatientHeader.vue';
import ProgramStatusBar from './components/ProgramStatusBar.vue';
import { getWeekList, getUserPackage, getCustomerInfo } from './services/services.js';
import { getLatestCheckupByPhoneBeforeDate, getPatientIdsByPhone } from '@/core/services/his.api.service';
import { hasPermission } from '@/core/services/permission.service';
import { PAGE_CODE, PERMISSION_TYPE } from '@/core/plugins/constants';

export default {
  name: 'CoachNoteDetail',
  components: {
    Tab1WeekCurrent,
    Tab2Progress,
    Tab3BaselineGoal,
    ProgressTracker,
    PatientHeader,
    ProgramStatusBar,
  },
  props: {
    userPackageId: {
      type: String,
      default: null,
    },
    phoneNumber: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedWeek: 1,
      activeTab: 0,
      weekList: [],
      weekListInitialized: false,
      hisBaselineData: null,
      selectedExternalId: null,
      patientIdOptions: [],
      showPatientSelectModal: false,
      pendingExternalId: null,
      patientInfo: {
        name: '',
        phone: '',
        birthYear: '',
        address: '',
        program: 'Quản lý đường huyết',
        doctor: '',
        healthCoach: 'Nguyễn Thị C',
        diagnosis: '',
      },
    };
  },
  computed: {
    packageStartDate() {
      return this.weekList.find(w => w.weekNumber === 0)?.weekStartDate ?? null;
    },
    isViewPermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.VIEW);
    },
    isInsertPermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.INSERT);
    },
    isUpdatePermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.UPDATE);
    },
    isDeletePermission() {
      return hasPermission(PAGE_CODE.COACH_NOTE, PERMISSION_TYPE.DELETE);
    },
  },
  mounted() {
    this.loadWeekList();
    this.loadUserPackage();
    this.loadExternalId();
  },
  methods: {
    async loadCustomerInfoFallback() {
      if (!this.userPackageId) return;
      try {
        const res = await getCustomerInfo(this.userPackageId);
        const info = res?.data ?? res;
        if (!info) return;
        this.patientInfo = {
          ...this.patientInfo,
          name: info.name || this.patientInfo.name,
          phone: info.phoneNumber || this.phoneNumber,
          birthYear: info.birthYear || this.patientInfo.birthYear,
          program: info.servicePackageName || this.patientInfo.program,
        };
      } catch (_) {}
    },
    async loadUserPackage() {
      if (!this.userPackageId) return;
      try {
        const res = await getUserPackage(this.userPackageId);
        const pkg = res?.data ?? res;
        if (pkg) {
          this.patientInfo = {
            ...this.patientInfo,
            program: pkg.tranServicePackageName || this.patientInfo.program,
            healthCoach: pkg.coachName || this.patientInfo.healthCoach,
          };
        }
      } catch (_) {}
    },
    async loadWeekList() {
      const res = await getWeekList(this.userPackageId);
      this.weekList = res?.data ?? res ?? [];
      // Chỉ auto-chọn tuần lần đầu; reload sau khi lưu giữ nguyên tuần đang xem
      if (!this.weekListInitialized) {
        const firstIncomplete = this.weekList.find(w => w.weekNumber > 0 && !w.isCompleted);
        this.selectedWeek = firstIncomplete ? firstIncomplete.weekNumber : 1;
        this.weekListInitialized = true;
      }
      if (this.selectedExternalId) {
        this.loadPatientInfoFromHis();
      }
    },
    extractDoctorName(notes) {
      const match = String(notes || '').match(/Bác sĩ:\s*([^,]+)/i);
      return match ? match[1].trim() : '';
    },
    extractPatientCode(raw) {
      if (!raw) return raw;
      const idx = raw.indexOf('_');
      return idx !== -1 ? raw.slice(idx + 1).trim() : raw.trim();
    },
    async loadExternalId() {
      if (!this.phoneNumber) return;
      try {
        const res = await getPatientIdsByPhone(this.phoneNumber);
        const ids = res?.data ?? [];
        if (ids.length === 1) {
          this.selectedExternalId = this.extractPatientCode(ids[0]);
          this.loadPatientInfoFromHis();
        } else if (ids.length > 1) {
          this.patientIdOptions = ids;
          this.pendingExternalId = ids[0];
          this.showPatientSelectModal = true;
        } else {
          // Không có hồ sơ HIS → báo cho người dùng và dùng customer-info
          this.$toastr.e({ title: 'Lỗi', msg: 'Khách hàng không có thông tin trong hệ thống HIS' });
          this.loadCustomerInfoFallback();
        }
      } catch (_) {
        this.loadCustomerInfoFallback();
      }
    },
    confirmSelectPatientId() {
      if (!this.pendingExternalId) return;
      this.selectedExternalId = this.extractPatientCode(this.pendingExternalId);
      this.showPatientSelectModal = false;
      this.loadPatientInfoFromHis();
    },
    async loadPatientInfoFromHis() {
      if (!this.selectedExternalId) return;
      const packageStartDate = this.weekList.find(w => w.weekNumber === 0)?.weekStartDate ?? null;
      if (!packageStartDate) return;
      try {
        const hisRes = await getLatestCheckupByPhoneBeforeDate(this.selectedExternalId, packageStartDate);
        this.hisBaselineData = hisRes;
        const his = hisRes?.data;
        if (!his || !his.piiData) {
          this.loadCustomerInfoFallback();
          return;
        }
        const pii = his.piiData || {};
        const visit = his.visits?.[0] || {};
        const emrHeader = his.emrHeaders?.[0] || {};
        const prescription = his.prescriptions?.[0] || {};
        const doctor = this.extractDoctorName(prescription.notes);
        this.patientInfo = {
          ...this.patientInfo,
          name: pii.fullName?.trim() || '',
          phone: this.phoneNumber,
          birthYear: pii.dateOfBirth ? new Date(pii.dateOfBirth).getFullYear() : '',
          address: pii.addressLine1 || '',
          doctor: doctor || this.patientInfo.doctor,
          diagnosis: visit.admissionDiagnosis || emrHeader.documentTitle || '',
        };
      } catch (_) {
        this.patientInfo = { ...this.patientInfo, phone: this.phoneNumber };
      }
    },
    onWeekSelect(weekNumber) {
      this.selectedWeek = weekNumber;
      this.activeTab = 0;
    },
    async refreshAll() {
      // Gọi lại toàn bộ API kể từ lúc chọn xong hồ sơ bệnh nhân
      await this.loadPatientInfoFromHis();
      if (this.$refs.tab1) {
        await this.$refs.tab1.loadWeekData();
      }
    },
  },
};
</script>

<style scoped>
.coach-note-page {
  padding: 24px;
  width: 100%;
}
.cn-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}
.cn-main {
  flex: 1;
  min-width: 0;
}
.cn-sidebar {
  width: 220px;
  flex-shrink: 0;
  position: sticky;
  top: 16px;
}
</style>

<style>
.cn-tabs .tab-content {
  min-height: calc(100vh - 220px);
}
</style>
